import { LoaderMessage } from "../../../../../../../components/loaderMessage/loaderMessage";
import CircularRefreshIcon from "../../../../../../../svg/circular-refresh-icon";
import { InfoIcon } from "../../../../../../../svg/info-icon";
import TickWithWavyCircle from "../../../../../../../svg/tickWithWavyCircle";
import {
  EvaluationApiResponse,
  ExecutionStatus,
  StageResult,
} from "../playGroundTypes";

import StageResultItem from "./StageResultItem";

interface ResultsPanelProps {
  isRunning: boolean;
  evaluationResult: EvaluationApiResponse | null;
  runError: string;
  expandedStageIds: Set<string>;
  onToggleExpand: (stageId: string) => void;
  onRefresh: () => void;
}

const mapApiStatus = (status: string): ExecutionStatus => {
  switch (status.toLowerCase()) {
    case "passed":
      return ExecutionStatus.PASSED;
    case "error":
      return ExecutionStatus.FAILED;
    case "skipped":
      return ExecutionStatus.SKIPPED;
    case "blocked":
      return ExecutionStatus.STOPPED;
    default:
      return ExecutionStatus.IDLE;
  }
};

const ResultsPanel = ({
  isRunning,
  evaluationResult,
  runError,
  expandedStageIds,
  onToggleExpand,
  onRefresh,
}: ResultsPanelProps) => {
  const hasEvaluationResult = evaluationResult !== null;

  return (
    <div className="flex h-full flex-col rounded-lg border border-primaryGray-14 bg-white shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3">
        <div className="flex items-center gap-2">
          <TickWithWavyCircle width="18" height="18" fillColor="#374151" />
          <span className="text-sm font-semibold text-gray-800">
            Execution Results
          </span>
        </div>
        {hasEvaluationResult && (
          <CircularRefreshIcon
            width="20"
            height="20"
            fill="#6b7280"
            onClick={onRefresh}
            className="cursor-pointer hover:opacity-70"
          />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {isRunning ? (
          <div className="flex h-full items-center justify-center py-16">
            <LoaderMessage message="Evaluating first STP review rules..." />
          </div>
        ) : runError ? (
          <div className="flex h-full flex-col items-center justify-center px-4 py-16 text-center">
            <div className="text-sm font-medium text-red-500">
              Error running evaluation
            </div>
            <div className="mt-1 text-xs text-red-400">{runError}</div>
          </div>
        ) : hasEvaluationResult ? (
          <div className="flex h-full flex-1 flex-col">
            {/* Blocks */}
            {evaluationResult.details?.blocks?.map((block) => (
              <div key={block.block_label} className="flex flex-1 flex-col">
                {/* Block label */}
                <div className="flex items-center justify-between bg-gray-50 px-4 py-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {block.block_label}
                  </span>
                  {block.status === "skipped" && (
                    <span className="rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-600">
                      Inactive
                    </span>
                  )}
                </div>

                {block.status === "skipped" && block.steps.length === 0 ? (
                  <div className="flex flex-1 flex-col items-center justify-center">
                    <span className="text-sm font-medium text-gray-500">
                      This block is currently inactive
                    </span>
                    <span className="mt-1 text-xs text-gray-400">
                      Enable it in the configuration to include it in
                      evaluations.
                    </span>
                  </div>
                ) : (
                  block.steps.map((step) => {
                    const mappedStatus = mapApiStatus(step.status);
                    const stageResult: StageResult = {
                      stageId: step.step_id,
                      stageName: step.label,
                      status: mappedStatus,
                      detail: step.reason
                        ? {
                            actionTriggered: step.label,
                            description: step.reason,
                          }
                        : undefined,
                    };
                    return (
                      <StageResultItem
                        key={step.step_id}
                        result={stageResult}
                        isExpanded={expandedStageIds.has(step.step_id)}
                        onToggleExpand={onToggleExpand}
                      />
                    );
                  })
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="flex h-full flex-col items-center justify-center text-gray-400">
            <InfoIcon width="24" height="24" stroke="#6b7280" />
            <div className="mt-3 text-sm">No results yet</div>
            <div className="text-xs">
              Fetch an order and click Run to evaluate
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResultsPanel;
